import { newIdempotencyKey } from "@station/api-client";
import {
  GUARANTEE_DIRECTION_LABELS,
  GUARANTEE_DIRECTIONS,
  GUARANTEE_KIND_LABELS,
  GUARANTEE_KINDS,
  GUARANTEE_STATUS_LABELS,
  type Guarantee,
  type GuaranteeDirection,
  type GuaranteeKind,
  type GuaranteeStatus,
  guaranteeStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DateInput,
  Dialog,
  EmptyState,
  FormField,
  Input,
  MoneyInput,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FileBadge, Plus, Upload } from "lucide-react";
import { type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import {
  CounterpartyCombobox,
  type CounterpartyOption,
} from "../../../features/counterparties/counterparty-combobox.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { uploadFile } from "../../../features/station-import.js";
import { useBanks } from "../../../features/treasury/definitions.js";
import { GuaranteeStatusBadge, TreasuryPage, useRefreshTreasury } from "../../../features/treasury/shared.js";
import { minorOf } from "../../../features/treasury/treasury-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/finans/teminatlar")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: GuaranteesPage,
});

function GuaranteesPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [creating, setCreating] = useState(false);
  return (
    <TreasuryPage
      title={t("menu.guarantees")}
      description={t("treasury.guarantees.description")}
      actions={
        permissions.has("treasury.guarantee.manage") ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("treasury.guarantees.create")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <Guarantees companyId={companyId} creating={creating} onCreatingChange={setCreating} />
      )}
    </TreasuryPage>
  );
}

function Guarantees({
  companyId,
  creating,
  onCreatingChange,
}: {
  companyId: string;
  creating: boolean;
  onCreatingChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const canManage = usePermissions().has("treasury.guarantee.manage");
  const [direction, setDirection] = useState<GuaranteeDirection | "">("");
  const [status, setStatus] = useState<GuaranteeStatus | "">("");
  const [editing, setEditing] = useState<Guarantee | null>(null);
  const [changing, setChanging] = useState<Guarantee | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = {
    companyId,
    limit: 25,
    ...(direction ? { direction } : {}),
    ...(status ? { status } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const guarantees = useQuery({
    queryKey: queryKeys.treasury(companyId, "guarantees", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/guarantees", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  return (
    <div className="grid gap-5">
      <GuaranteeSummary companyId={companyId} />

      <div className="flex flex-wrap items-end gap-2">
        <FormField label={t("treasury.guarantees.direction")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={direction}
              onChange={(event) => setDirection(event.target.value as GuaranteeDirection | "")}
            >
              <option value="">{t("common.all")}</option>
              {GUARANTEE_DIRECTIONS.map((value) => (
                <option key={value} value={value}>
                  {GUARANTEE_DIRECTION_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("common.status")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={status}
              onChange={(event) =>
                setStatus(event.target.value === "" ? "" : guaranteeStatusSchema.parse(event.target.value))
              }
            >
              <option value="">{t("common.all")}</option>
              {guaranteeStatusSchema.options.map((value) => (
                <option key={value} value={value}>
                  {GUARANTEE_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {guarantees.error ? (
        <Alert tone="danger">{errorMessage(guarantees.error)}</Alert>
      ) : guarantees.isPending ? (
        <SkeletonRows rows={5} />
      ) : guarantees.data.items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<FileBadge />}
          title={t("treasury.guarantees.emptyTitle")}
          description={t("treasury.guarantees.emptyDescription")}
        />
      ) : (
        <SimpleTable
          caption={t("menu.guarantees")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("treasury.guarantees.direction")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.fields.kind")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.fields.bank")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.guarantees.beneficiary")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.fields.amount")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.guarantees.expiry")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.guarantees.nextCommission")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.status")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.actions")}
              </th>
            </tr>
          }
        >
          {guarantees.data.items.map((guarantee) => (
            <tr key={guarantee.id} className="border-t align-top">
              <td className={tdClass}>{GUARANTEE_DIRECTION_LABELS[guarantee.direction]}</td>
              <td className={tdClass}>
                {GUARANTEE_KIND_LABELS[guarantee.kind]}
                {guarantee.referenceNo ? (
                  <>
                    <br />
                    <span className="font-mono text-xs text-muted-foreground">{guarantee.referenceNo}</span>
                  </>
                ) : null}
              </td>
              <td className={tdClass}>{guarantee.bankName ?? "—"}</td>
              <td className={tdClass}>{guarantee.counterpartyName}</td>
              <td className={tdRightClass}>
                <MoneyText value={guarantee.amount} className="font-medium" />
              </td>
              <td className={tdClass}>
                {guarantee.expiryDate ? (
                  <span className="grid gap-1">
                    <span className="tabular">{formatDate(guarantee.expiryDate)}</span>
                    {guarantee.daysToExpiry !== null ? (
                      <StatusBadge
                        tone={
                          guarantee.daysToExpiry < 0
                            ? "danger"
                            : guarantee.daysToExpiry <= 30
                              ? "warning"
                              : "neutral"
                        }
                      >
                        {guarantee.daysToExpiry < 0
                          ? t("treasury.guarantees.expired")
                          : t("treasury.guarantees.daysLeft", { days: guarantee.daysToExpiry })}
                      </StatusBadge>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t("treasury.guarantees.noExpiry")}</span>
                )}
              </td>
              <td className={tdClass}>
                {guarantee.nextCommissionDate ? (
                  <span className="grid gap-0.5">
                    <span className="tabular">{formatDate(guarantee.nextCommissionDate)}</span>
                    {guarantee.nextCommissionAmount ? (
                      <MoneyText value={guarantee.nextCommissionAmount} className="text-xs" />
                    ) : null}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className={tdClass}>
                <GuaranteeStatusBadge status={guarantee.status} />
                {guarantee.memoPosted ? (
                  <>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      {t("treasury.guarantees.memoPosted")}
                    </span>
                  </>
                ) : null}
              </td>
              <td className={tdClass}>
                {canManage ? (
                  <span className="flex flex-wrap gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(guarantee)}>
                      {t("common.edit")}
                    </Button>
                    {guarantee.status === "ACTIVE" ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setChanging(guarantee)}>
                          {t("treasury.guarantees.changeStatus")}
                        </Button>
                        {guarantee.nextCommissionDate ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={busyId === guarantee.id}
                            onClick={() => {
                              setBusyId(guarantee.id);
                              setError(null);
                              void api
                                .POST("/guarantees/{id}/advance-commission", {
                                  params: { path: { id: guarantee.id } },
                                  body: { version: guarantee.version },
                                })
                                .then(async () => {
                                  await refresh(companyId);
                                  toast({
                                    tone: "success",
                                    title: t("treasury.guarantees.commissionAdvanced"),
                                  });
                                })
                                .catch((caught: unknown) => setError(errorMessage(caught)))
                                .finally(() => setBusyId(null));
                            }}
                          >
                            {t("treasury.guarantees.advanceCommission")}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </SimpleTable>
      )}

      {pages.hasPrevious || guarantees.data?.page.hasMore ? (
        <nav className="flex items-center justify-end gap-2" aria-label={t("treasury.guarantees.pagination")}>
          <Button variant="outline" size="sm" onClick={pages.previous} disabled={!pages.hasPrevious}>
            {t("treasury.counts.previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => pages.next(guarantees.data?.page.nextCursor)}
            disabled={!guarantees.data?.page.hasMore}
          >
            {t("treasury.counts.next")}
          </Button>
        </nav>
      ) : null}

      <GuaranteeDialog
        companyId={companyId}
        open={creating || editing !== null}
        guarantee={editing}
        onClose={() => {
          onCreatingChange(false);
          setEditing(null);
        }}
      />
      <StatusDialog companyId={companyId} guarantee={changing} onClose={() => setChanging(null)} />
    </div>
  );
}

/** Dağıtıcı bazında verilen ↔ sözleşme gereği istenen teminat; açık (shortfall) vurgulanır ve buradan kaydedilir. */
function GuaranteeSummary({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const canManage = usePermissions().has("treasury.guarantee.manage");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [required, setRequired] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const summary = useQuery({
    queryKey: queryKeys.treasury(companyId, "guarantee-summary"),
    queryFn: async () => (await api.GET("/guarantees/summary", { params: { query: { companyId } } })).data!,
  });

  async function save(counterpartyId: string): Promise<void> {
    setBusy(true);
    try {
      await api.PUT("/guarantees/requirements", {
        body: { companyId, counterpartyId, requiredAmount: minorOf(required).toString(), note: null },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.guarantees.requirementSaved") });
      setEditingId(null);
    } catch (caught) {
      toast({ tone: "danger", title: errorMessage(caught) });
    } finally {
      setBusy(false);
    }
  }

  if (summary.isPending) return <SkeletonRows rows={2} />;
  if (summary.error) return <Alert tone="danger">{errorMessage(summary.error)}</Alert>;
  if (summary.data.rows.length === 0)
    return <Alert tone="info">{t("treasury.guarantees.summaryEmpty")}</Alert>;

  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("treasury.guarantees.summaryTitle")}</h2>
      <SimpleTable
        caption={t("treasury.guarantees.summaryTitle")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("treasury.guarantees.beneficiary")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("treasury.guarantees.givenActive")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("treasury.guarantees.requiredAmount")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("treasury.guarantees.shortfall")}
            </th>
            <th scope="col" className={thClass}>
              {t("treasury.guarantees.nearestExpiry")}
            </th>
            <th scope="col" className={thClass}>
              {t("common.actions")}
            </th>
          </tr>
        }
      >
        {summary.data.rows.map((row) => (
          <tr key={row.counterpartyId} className="border-t align-top">
            <td className={tdClass}>{row.counterpartyName}</td>
            <td className={tdRightClass}>
              <MoneyText value={row.givenActive} />
            </td>
            <td className={tdRightClass}>
              {editingId === row.counterpartyId ? (
                <MoneyInput
                  className="h-9"
                  aria-label={t("treasury.guarantees.requiredAmountFor", { name: row.counterpartyName })}
                  value={required}
                  onValueChange={setRequired}
                />
              ) : row.requiredAmount === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <MoneyText value={row.requiredAmount} />
              )}
            </td>
            <td className={tdRightClass}>
              {row.shortfall && row.shortfall !== "0" ? (
                <span className="inline-flex items-center gap-2">
                  <StatusBadge tone="danger">{t("treasury.guarantees.shortfallBadge")}</StatusBadge>
                  <MoneyText value={row.shortfall} className="font-medium text-destructive" />
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className={`${tdClass} tabular`}>{formatDate(row.nearestExpiry)}</td>
            <td className={tdClass}>
              {!canManage ? (
                <span className="text-muted-foreground">—</span>
              ) : editingId === row.counterpartyId ? (
                <span className="flex gap-1">
                  <Button size="sm" loading={busy} onClick={() => void save(row.counterpartyId)}>
                    {t("common.save")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} disabled={busy}>
                    {t("common.cancel")}
                  </Button>
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingId(row.counterpartyId);
                    setRequired(row.requiredAmount);
                  }}
                >
                  {t("treasury.guarantees.setRequirement")}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </SimpleTable>
    </Card>
  );
}

interface GuaranteeFormState {
  kind: GuaranteeKind;
  direction: GuaranteeDirection;
  bankId: string;
  counterparty: CounterpartyOption | null;
  referenceNo: string;
  amount: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  commissionRate: string;
  commissionPeriodMonths: string;
  nextCommissionDate: string | null;
  description: string;
  attachmentFileId: string | null;
}

function GuaranteeDialog({
  companyId,
  open,
  guarantee,
  onClose,
}: {
  companyId: string;
  open: boolean;
  guarantee: Guarantee | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const banks = useBanks();
  const fileRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<GuaranteeFormState>(() => blankGuarantee());
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "upload" | null>(null);
  const [lastId, setLastId] = useState<string | null>(null);

  function blankGuarantee(): GuaranteeFormState {
    return {
      kind: "LETTER",
      direction: "GIVEN",
      bankId: "",
      counterparty: null,
      referenceNo: "",
      amount: null,
      issueDate: todayIstanbul(),
      expiryDate: null,
      commissionRate: "",
      commissionPeriodMonths: "",
      nextCommissionDate: null,
      description: "",
      attachmentFileId: null,
    };
  }

  if (open && (guarantee?.id ?? null) !== lastId) {
    setLastId(guarantee?.id ?? null);
    setError(null);
    setFileName(guarantee?.attachmentName ?? null);
    setState(
      guarantee
        ? {
            kind: guarantee.kind,
            direction: guarantee.direction,
            bankId: guarantee.bankId ?? "",
            counterparty: { id: guarantee.counterpartyId, code: "", name: guarantee.counterpartyName },
            referenceNo: guarantee.referenceNo ?? "",
            amount: guarantee.amount,
            issueDate: guarantee.issueDate,
            expiryDate: guarantee.expiryDate,
            commissionRate: guarantee.commissionRate ?? "",
            commissionPeriodMonths:
              guarantee.commissionPeriodMonths === null ? "" : String(guarantee.commissionPeriodMonths),
            nextCommissionDate: guarantee.nextCommissionDate,
            description: guarantee.description ?? "",
            attachmentFileId: null,
          }
        : blankGuarantee(),
    );
  }

  async function submit(): Promise<void> {
    if (!state.counterparty) return setError(t("treasury.guarantees.pickCounterparty"));
    if (!state.issueDate) return setError(t("treasury.guarantees.pickIssueDate"));
    setBusy("save");
    setError(null);
    // Boş bırakılan isteğe bağlı alanlar gövdeye null gider (boş dizge doğrulamaya takılır).
    const common = {
      bankId: state.bankId || null,
      bankName: null,
      counterpartyId: state.counterparty.id,
      referenceNo: state.referenceNo.trim() || null,
      issueDate: state.issueDate,
      expiryDate: state.expiryDate,
      commissionRate: state.commissionRate.trim().replace(",", ".") || null,
      commissionPeriodMonths: state.commissionPeriodMonths ? Number(state.commissionPeriodMonths) : null,
      nextCommissionDate: state.nextCommissionDate,
      description: state.description.trim() || null,
      attachmentFileId: state.attachmentFileId,
    };
    try {
      if (guarantee)
        await api.PATCH("/guarantees/{id}", {
          params: { path: { id: guarantee.id } },
          body: { ...common, version: guarantee.version },
        });
      else
        await api.POST("/guarantees", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body: {
            ...common,
            companyId,
            kind: state.kind,
            direction: state.direction,
            amount: minorOf(state.amount).toString(),
          },
        });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.guarantees.saved") });
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && busy === null && onClose()}
      title={guarantee ? t("treasury.guarantees.edit") : t("treasury.guarantees.create")}
      description={t("treasury.guarantees.dialogDescription")}
      className="sm:max-w-3xl"
      dismissible={busy === null}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label={t("treasury.fields.kind")} required>
            {(control) => (
              <Select
                {...control}
                value={state.kind}
                disabled={guarantee !== null}
                onChange={(event) => setState({ ...state, kind: event.target.value as GuaranteeKind })}
              >
                {GUARANTEE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {GUARANTEE_KIND_LABELS[kind]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("treasury.guarantees.direction")} required>
            {(control) => (
              <Select
                {...control}
                value={state.direction}
                disabled={guarantee !== null}
                onChange={(event) =>
                  setState({ ...state, direction: event.target.value as GuaranteeDirection })
                }
              >
                {GUARANTEE_DIRECTIONS.map((value) => (
                  <option key={value} value={value}>
                    {GUARANTEE_DIRECTION_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("treasury.fields.bank")}>
            {(control) => (
              <Select
                {...control}
                value={state.bankId}
                onChange={(event) => setState({ ...state, bankId: event.target.value })}
              >
                <option value="">{t("common.select")}</option>
                {(banks.data ?? []).map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.shortName}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField
            label={t("treasury.guarantees.beneficiary")}
            description={t("treasury.guarantees.beneficiaryHint")}
            className="sm:col-span-2"
            required
          >
            {(control) => (
              <CounterpartyCombobox
                {...control}
                companyId={companyId}
                value={state.counterparty}
                onChange={(counterparty) => setState({ ...state, counterparty })}
              />
            )}
          </FormField>
          <FormField label={t("treasury.guarantees.referenceNo")}>
            {(control) => (
              <Input
                {...control}
                value={state.referenceNo}
                className="font-mono"
                maxLength={60}
                onChange={(event) => setState({ ...state, referenceNo: event.target.value })}
              />
            )}
          </FormField>
          <FormField label={t("treasury.fields.amount")} required>
            {(control) => (
              <MoneyInput
                {...control}
                value={state.amount}
                disabled={guarantee !== null}
                onValueChange={(amount) => setState({ ...state, amount })}
              />
            )}
          </FormField>
          <FormField label={t("treasury.guarantees.issueDate")} required>
            {(control) => (
              <DateInput
                {...control}
                value={state.issueDate}
                onValueChange={(issueDate) => setState({ ...state, issueDate })}
              />
            )}
          </FormField>
          <FormField
            label={t("treasury.guarantees.expiry")}
            description={t("treasury.guarantees.expiryHint")}
          >
            {(control) => (
              <DateInput
                {...control}
                value={state.expiryDate}
                onValueChange={(expiryDate) => setState({ ...state, expiryDate })}
              />
            )}
          </FormField>
          <FormField label={t("treasury.guarantees.commissionRate")}>
            {(control) => (
              <Input
                {...control}
                value={state.commissionRate}
                inputMode="decimal"
                onChange={(event) => setState({ ...state, commissionRate: event.target.value })}
              />
            )}
          </FormField>
          <FormField label={t("treasury.guarantees.commissionPeriod")}>
            {(control) => (
              <Input
                {...control}
                value={state.commissionPeriodMonths}
                inputMode="numeric"
                onChange={(event) =>
                  setState({
                    ...state,
                    commissionPeriodMonths: event.target.value.replace(/\D/g, "").slice(0, 2),
                  })
                }
              />
            )}
          </FormField>
          <FormField label={t("treasury.guarantees.nextCommissionDate")}>
            {(control) => (
              <DateInput
                {...control}
                value={state.nextCommissionDate}
                onValueChange={(nextCommissionDate) => setState({ ...state, nextCommissionDate })}
              />
            )}
          </FormField>
        </div>

        <FormField label={t("treasury.fields.description")}>
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={500}
              value={state.description}
              onChange={(event) => setState({ ...state, description: event.target.value })}
            />
          )}
        </FormField>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            loading={busy === "upload"}
            disabled={busy !== null}
          >
            <Upload aria-hidden="true" />
            {t("treasury.guarantees.attachment")}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="sr-only"
            tabIndex={-1}
            aria-label={t("treasury.guarantees.attachment")}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setBusy("upload");
              void uploadFile(file, "DOCUMENT")
                .then((id) => {
                  setState((current) => ({ ...current, attachmentFileId: id }));
                  setFileName(file.name);
                })
                .catch((caught: unknown) => setError(errorMessage(caught)))
                .finally(() => {
                  setBusy(null);
                  if (fileRef.current) fileRef.current.value = "";
                });
            }}
          />
          {fileName ? <span className="text-sm text-muted-foreground">{fileName}</span> : null}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy !== null}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy === "save"}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** İade / nakde çevirme: nazım kaydı varsa sunucu ters kaydını alır. */
function StatusDialog({
  companyId,
  guarantee,
  onClose,
}: {
  companyId: string;
  guarantee: Guarantee | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const [status, setStatus] = useState<"RETURNED" | "CALLED">("RETURNED");
  const [statusDate, setStatusDate] = useState<string | null>(todayIstanbul());
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    if (!guarantee || !statusDate) return;
    setBusy(true);
    setError(null);
    try {
      await api.POST("/guarantees/{id}/status", {
        params: { path: { id: guarantee.id } },
        body: { status, statusDate, note: note.trim() || null, version: guarantee.version },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.guarantees.statusChanged") });
      setNote("");
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={guarantee !== null}
      onOpenChange={(next) => !next && !busy && onClose()}
      title={t("treasury.guarantees.changeStatus")}
      description={t("treasury.guarantees.changeStatusDescription")}
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("common.status")} required>
          {(control) => (
            <Select
              {...control}
              value={status}
              onChange={(event) => setStatus(event.target.value as "RETURNED" | "CALLED")}
            >
              <option value="RETURNED">{GUARANTEE_STATUS_LABELS.RETURNED}</option>
              <option value="CALLED">{GUARANTEE_STATUS_LABELS.CALLED}</option>
            </Select>
          )}
        </FormField>
        <FormField label={t("treasury.guarantees.statusDate")} required>
          {(control) => <DateInput {...control} value={statusDate} onValueChange={setStatusDate} />}
        </FormField>
        <FormField label={t("treasury.guarantees.statusNote")}>
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
